<template>
  <b-card no-body>
    <b-card-body class="search-panel">
      <div class="search-panel__title">
        <span class="pr-1">
          Vessel:
        </span>
        <template v-if="searchValue && searchValue.imo">
          <CButton color="success" v-on:click="handleSearchResultRemoved">
            <h4 class="m-0"><strong>{{ searchValue.imo }} - {{ searchValue.name }}</strong></h4>
          </CButton>
        </template>
        <template v-else>
          <i>No vessel selected, search for a vessel/imo ----------> </i>
        </template>
      </div>

      <div class="search-panel__controls">
        <!-- date selector -->
        <date-range-picker-custom
          @input="handleDateRangeChanged"
          class="mr-2"
          label="Tracking period:"
          :options="{ranges: datePickerRanges}"
          :startDate.sync="startDate"
          :endDate.sync="endDate"
          :disabled="apiRequestIsPerforming" />

        <!-- vessel search -->
        <vessel-search @input="handleVesselChanged" />
      </div>

    </b-card-body>
  </b-card>
</template>

<script>
import moment from 'moment';
import debounce from 'lodash-es/debounce';
import getNested from 'lodash-es/get';

import { mapActions, mapGetters } from 'vuex';
import {
  getCurrentQuarterRange,
  getCurrentYear,
  getToday,
  getYearToDateRange
} from '@/shared/date-utils';

import DateRangePickerCustom from '../widgets/DateRangePickerCustom';
import VesselSearch from '../widgets/VesselSearch';


const debounceDelay = 100;

export default {
  name: 'search-panel',

  components: {
    VesselSearch,
    DateRangePickerCustom,
  },

  data() {
    return {
      imo: [],
      apiRequestIsPerforming: false,
      searchLoading: false,
      searchValue: null,
      startDate: getToday().subtract(7, 'days').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
    };
  },

  methods: {
    ...mapActions('api/presets', [
      'updateCurrentFilterSettings',
    ]),

    ...mapActions('api/fleets', [
      'searchVessels',
    ]),


    handleVesselChanged(vessel) {
      this.searchValue = vessel;
      this.$emit('input', this.searchValue, this.getExtraParams());
    },

    handleSearchResultRemoved() {
      this.$nextTick(() => {
        this.searchValue = null;
        this.imo = []
        this.$emit('input', this.searchValue, this.getExtraParams());
      });
    },

    handleDateRangeChanged() {
      this.$emit('input', this.searchValue, this.getExtraParams());
    },

    computeExName(vessel) {
      if (vessel.exName) {
        return '(Ex name: ' + vessel.exName + ')';
      } else {
        return '';
      }
    },

    getExtraParams() {
      return {
        startDate: this.startDate,
        endDate: moment(this.endDate).utc().add(2, 'days').format('YYYY-MM-DD'),
      };
    },

    applyFilterPreset(preset) {
      this.searchValue = getNested(preset, 'params.searchPanel.searchValue', null);
      this.startDate = getNested(preset, 'params.searchPanel.startDate', null);
      this.endDate = getNested(preset, 'params.searchPanel.endDate', null);
      this.$emit('input', this.searchValue, this.getExtraParams());
    },
  },

  computed: {
    ...mapGetters('api', ['getActiveRequestsCount']),
    ...mapGetters('api/presets', ['getActivePreset']),

    searchFields() {
      return {
        searchValue: this.searchValue,
        startDate: this.startDate,
        endDate: this.endDate,
      };
    },

    datePickerRanges() {
      let ranges = {
        'Full period (2015-today)': [
          moment('2015-01-01').toDate(),
          getToday().toDate(),
        ],
        'QTD': getCurrentQuarterRange(),
        'YTD': getYearToDateRange(),
      };

      const currentYear = getCurrentYear();
      for(let year = currentYear - 3; year < currentYear; year++) {
        ranges[`${year}`]  = [
          moment(`${year}-01-01`).toDate(),
          moment(`${year}-12-31`).toDate(),
        ];
      }

      return ranges;
    },
  },

  watch: {
    getActiveRequestsCount: debounce(function (newValue) {
      this.apiRequestIsPerforming = !!newValue;
    }, debounceDelay),

    searchFields: {
      // track current filters to presets subsystem
      deep: true,
      handler: async function() {
        await this.updateCurrentFilterSettings({
          path: this.$route.path,
          parameters: {
            searchPanel: this.searchFields,
          },
        });
      },
    },

    getActivePreset: {
      immediate: true,
      handler: async function (preset) {
        if(preset && this.$route.path !== preset.viewName) {
          return;
        }

        if(preset) {
          this.applyFilterPreset(preset);
        }
      },
    },
  },
}
</script>

<style scoped lang="scss">
  .search-panel {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__title {
      font-size: 18px;

    }
    &__controls {
      display: flex;
      align-items: center;

      .search-multiselect {
        margin-right: 20px;
        transition: all 0.2s;
        min-width: 300px;

        &__option {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }
      }
    }
  }

  .choice-button,
  .choice-button:focus,
  .choice-button:active {
    min-width: 60px;
    outline: none !important;
    box-shadow: none !important;
    background-color: #1d3d4f !important;
    border-color: #1d3d4f !important;

    &.active {
      background-color: #3b89a1 !important;
      border-color: #3b89a1 !important;
    }
  }

</style>
